import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Header, Footer } from "../components/layout";
import { AppDownloadBanner } from "../components/sections";
import { SEO } from "../components/SEO";
import { buildLocalBusinessSchema } from "../utils/structuredData";
import { APP_STORE_URL, PLAY_STORE_URL, getSmartAppDownloadUrl } from "../utils/appLinks";
import { parseCoordinates, findCoordinatesForAddress } from "../utils/locations";
import { getBusinessBySlug } from "../apis/businesses";
import type { BusinessDetail as BusinessDetailType, BusinessOffer, ShopProduct, Branch, Review } from "../apis/businesses";

export default function BusinessDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [business, setBusiness] = useState<BusinessDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Interactive UI state
  const [selectedBranchIndex, setSelectedBranchIndex] = useState(0);
  const [selectedOffer, setSelectedOffer] = useState<BusinessOffer | null>(null);
  const [lightboxImageIndex, setLightboxImageIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    getBusinessBySlug(slug)
      .then((data) => {
        if (!cancelled) {
          setBusiness(data);
          setSelectedBranchIndex(0);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Shop not found");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FC] text-gray-900">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pt-24">
          <div className="h-6 w-48 bg-gray-200 animate-pulse rounded-md mb-6" />
          <div className="h-72 sm:h-96 w-full bg-gray-200 animate-pulse rounded-3xl mb-6" />
          <div className="h-32 w-full bg-white animate-pulse rounded-2xl shadow-sm mb-8" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8 space-y-6">
              <div className="h-40 bg-white animate-pulse rounded-2xl" />
              <div className="h-64 bg-white animate-pulse rounded-2xl" />
            </div>
            <div className="lg:col-span-4 space-y-6">
              <div className="h-48 bg-white animate-pulse rounded-2xl" />
              <div className="h-40 bg-white animate-pulse rounded-2xl" />
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="min-h-screen bg-[#F8F9FC] text-gray-900">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-28 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 text-red-600 flex items-center justify-center text-2xl font-bold">
            !
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">
            Shop Not Found
          </h1>
          <p className="text-gray-600 mb-6">
            {error || "The shop you are looking for is either unavailable or has been moved."}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/shops"
              className="px-6 py-2.5 rounded-full bg-[#18133E] text-white font-medium hover:bg-[#251E5C] transition-colors"
            >
              Browse all shops
            </Link>
            <Link
              to="/deals"
              className="px-6 py-2.5 rounded-full bg-white border border-gray-300 text-gray-800 font-medium hover:bg-gray-50 transition-colors"
            >
              Explore Deals
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Extract business fields with rich defaults
  const {
    businessDetails,
    businessInfo,
    serviceCategories = [],
    branches = [],
    offers = [],
    products = [],
    reviews = [],
  } = business;

  const name = businessDetails?.businessName || "Store Details";
  const categoryName =
    typeof businessDetails?.businessType === "string"
      ? businessDetails.businessType
      : (businessDetails?.businessType as any)?.name ||
        serviceCategories[0] ||
        "Store";

  const primaryCategoryPill = serviceCategories[0] || categoryName;

  const logo = businessInfo?.businessLogo || "/deals/shop_glow_salon.jpg";
  const coverImage = businessInfo?.coverImage || businessInfo?.businessImages?.[0] || "/deals/shop_glow_salon.jpg";
  const description =
    businessInfo?.description ||
    `${name} is your partner destination on SetGo.`;
  const rating = businessInfo?.rating && businessInfo.rating > 0 ? businessInfo.rating : null;
  const reviewCount = businessInfo?.reviewCount || reviews?.length || 0;

  // Branch handling
  const activeBranchList: Branch[] = branches && branches.length > 0 ? branches : [];
  const currentBranch = activeBranchList[selectedBranchIndex] || null;
  const rawBizAddr = businessDetails?.address;
  const formattedBizAddr =
    typeof rawBizAddr === "string"
      ? rawBizAddr
      : typeof rawBizAddr === "object" && rawBizAddr
      ? [(rawBizAddr as any).line1, (rawBizAddr as any).city, (rawBizAddr as any).district]
          .filter(Boolean)
          .join(", ")
      : (businessDetails as any)?.district || "";

  const activeAddress =
    currentBranch?.location?.address ||
    formattedBizAddr ||
    "";
  const activePhone =
    currentBranch?.contactNumber || businessInfo?.contactPhone || "";

  // Resolve exact GPS coordinates from branch, partner storeLocation, or address matching
  let activeCoordinates: { lat: number; lng: number } | null = null;
  const branchCoords = parseCoordinates(currentBranch?.location?.coordinates);
  if (branchCoords) {
    activeCoordinates = branchCoords;
  } else {
    const partnerCoords =
      parseCoordinates((businessInfo?.storeLocation as any)?.coordinates) ||
      parseCoordinates((businessInfo as any)?.coordinates) ||
      parseCoordinates((businessDetails as any)?.storeLocation?.coordinates) ||
      parseCoordinates((businessDetails as any)?.location?.coordinates) ||
      parseCoordinates((business as any)?.location?.coordinates);

    if (partnerCoords) {
      activeCoordinates = partnerCoords;
    } else {
      for (const b of activeBranchList) {
        const bc = parseCoordinates(b.location?.coordinates);
        if (bc) {
          activeCoordinates = bc;
          break;
        }
      }

      if (!activeCoordinates) {
        const addressCandidate = [
          currentBranch?.location?.city,
          currentBranch?.location?.district,
          currentBranch?.location?.address,
          (businessInfo?.storeLocation as any)?.city,
          (businessInfo?.storeLocation as any)?.district,
          activeAddress,
          formattedBizAddr,
        ]
          .filter(Boolean)
          .join(", ");

        activeCoordinates = findCoordinatesForAddress(addressCandidate);
      }
    }
  }

  const mapsQueryUrl = activeCoordinates
    ? `https://www.google.com/maps/search/?api=1&query=${activeCoordinates.lat},${activeCoordinates.lng}`
    : activeAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(activeAddress)}`
    : "#";

  const mapsDirectionsUrl = activeCoordinates
    ? `https://www.google.com/maps/dir/?api=1&destination=${activeCoordinates.lat},${activeCoordinates.lng}`
    : mapsQueryUrl;

  // Gallery images from API
  const activeGallery: string[] = businessInfo?.businessImages || [];

  // Products from API
  const activeProducts: ShopProduct[] = products || [];

  // Offers from API
  const activeOffers: BusinessOffer[] = offers || [];

  // Reviews from API
  const activeReviews: Review[] = reviews || [];

  // Social Links
  const socialLinks = businessInfo?.socialLinks;

  const canonicalSlug = business.slug || slug || "";
  const structuredData = buildLocalBusinessSchema(name, canonicalSlug, {
    description,
    image: logo,
    address: activeAddress || "Kerala, India",
    telephone: activePhone || "+91",
    rating: rating || 5,
    reviewCount: reviewCount || 1,
    areaServed: currentBranch?.location?.city || "Kerala",
    ...(activeCoordinates && {
      geo: {
        latitude: activeCoordinates.lat,
        longitude: activeCoordinates.lng,
      },
    }),
  });

  return (
    <div className="min-h-screen bg-[#F6F7FA] text-gray-900 font-sans selection:bg-purple-100 selection:text-purple-900">
      <SEO
        title={`${name} - Shop Details & Offers`}
        description={description}
        image={coverImage}
        url={`/shops/${canonicalSlug}`}
        type="website"
        structuredData={structuredData}
      />

      {/* Dark SetGo Navbar */}
      <Header />

      <main className="pt-20 sm:pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className="mb-4">
            <ol className="flex items-center space-x-2 text-xs sm:text-sm text-gray-500 font-medium">
              <li>
                <Link to="/deals" className="hover:text-purple-700 transition-colors">
                  Deals near you
                </Link>
              </li>
              <li>
                <span className="text-gray-300">/</span>
              </li>
              <li>
                <Link
                  to={`/shops?category=${encodeURIComponent(categoryName)}`}
                  className="hover:text-purple-700 transition-colors"
                >
                  {categoryName}
                </Link>
              </li>
              <li>
                <span className="text-gray-300">/</span>
              </li>
              <li className="text-gray-900 font-bold truncate max-w-[200px] sm:max-w-none">
                {name}
              </li>
            </ol>
          </nav>

          {/* Hero Storefront Image */}
          <div className="relative w-full h-[260px] sm:h-[360px] md:h-[440px] rounded-3xl overflow-hidden shadow-md bg-gray-900">
            <img
              src={coverImage}
              alt={name}
              className="w-full h-full object-cover object-center brightness-[0.92]"
            />
            {/* Top-left Category Pill */}
            <div className="absolute top-4 left-4 sm:top-6 sm:left-6">
              <span className="inline-flex items-center px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-white/95 text-gray-900 backdrop-blur-md shadow-md border border-white/40">
                {primaryCategoryPill}
              </span>
            </div>
          </div>

          {/* Floating Shop Header Card */}
          <div className="relative z-10 -mt-10 sm:-mt-14 mb-8">
            <div className="bg-white rounded-2xl md:rounded-3xl p-5 sm:p-7 shadow-xl border border-gray-100 transition-all">
              {/* Top Row: Logo, Name, Location, Buttons */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
                {/* Left: Avatar & Title info */}
                <div className="flex items-start sm:items-center gap-4 sm:gap-5 min-w-0">
                  <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-[#FFF9ED] border border-amber-200/80 p-2 flex items-center justify-center overflow-hidden shadow-sm">
                    {logo ? (
                      <img
                        src={logo}
                        alt={name}
                        className="w-full h-full object-cover rounded-xl"
                      />
                    ) : (
                      <span className="text-amber-600 font-black text-2xl">
                        {name.charAt(0)}
                      </span>
                    )}
                  </div>

                  <div className="min-w-0">
                    <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight truncate">
                      {name}
                    </h1>

                    <div className="flex flex-wrap items-center gap-2.5 mt-1.5 text-xs sm:text-sm text-gray-600">
                      <a
                        href={mapsQueryUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 truncate max-w-[280px] sm:max-w-md hover:text-purple-700 transition-colors"
                        title={activeCoordinates ? `Coordinates: ${activeCoordinates.lat.toFixed(5)}, ${activeCoordinates.lng.toFixed(5)}` : undefined}
                      >
                        <svg
                          className="w-4 h-4 text-gray-400 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        <span className="truncate">
                          {activeAddress ? activeAddress.split(",")[0] || activeAddress : "Kerala, India"}
                        </span>
                      </a>

                      {/* Rating Badge */}
                      {rating != null && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-bold bg-[#FEF6E7] text-[#925C0C] border border-[#FDE6BA]">
                          <span className="text-[#E59819]">★</span>
                          <span>{rating.toFixed(1)}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right: Action Buttons (Call & Directions) */}
                <div className="flex items-center gap-3 self-start md:self-center flex-shrink-0">
                  <a
                    href={`tel:${activePhone}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#108A4E] hover:bg-[#0D7240] text-white font-semibold text-xs sm:text-sm shadow-sm transition-all hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <svg
                      className="w-4 h-4 fill-current"
                      viewBox="0 0 20 20"
                    >
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 4V3z" />
                    </svg>
                    Call
                  </a>

                  <a
                    href={mapsDirectionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#EFEBFA] hover:bg-[#E3DCF7] text-[#552FB5] font-semibold text-xs sm:text-sm transition-all hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <svg
                      className="w-4 h-4 stroke-current fill-none"
                      viewBox="0 0 24 24"
                      strokeWidth={2.2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                      />
                    </svg>
                    Directions
                  </a>
                </div>
              </div>

              {/* Bottom Row: Branches Selector */}
              {activeBranchList.length > 0 && (
                <div className="mt-5 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-500 mr-1">
                    Branches
                  </span>
                  <div className="flex flex-wrap items-center gap-2">
                    {activeBranchList.map((branch, idx) => {
                      const isActive = idx === selectedBranchIndex;
                      return (
                        <button
                          key={branch._id || idx}
                          type="button"
                          onClick={() => setSelectedBranchIndex(idx)}
                          className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-all ${
                            isActive
                              ? "bg-[#E6F4EA] text-[#0D652D] border border-[#34A853] shadow-sm"
                              : "bg-[#F1F3F6] text-gray-700 hover:bg-gray-200 border border-transparent"
                          }`}
                        >
                          {branch.branchName || `Branch ${idx + 1}`}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Main 2-Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Left Column (Main Details: About, Offers, Products, Gallery) */}
            <div className="lg:col-span-8 space-y-6">
              {/* About Section */}
              <div className="bg-white rounded-2xl md:rounded-3xl p-6 sm:p-7 shadow-sm border border-gray-100">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
                  About
                </h2>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base font-normal">
                  {description}
                </p>
              </div>

              {/* Offers Section */}
              <div className="bg-white rounded-2xl md:rounded-3xl p-6 sm:p-7 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                    Offers
                  </h2>
                  <span className="text-xs sm:text-sm font-semibold text-[#108A4E] bg-[#EAF8F0] px-3 py-1 rounded-full border border-[#BDE7CE]">
                    {activeOffers.length} live
                  </span>
                </div>

                {activeOffers.length === 0 ? (
                  <div className="py-8 text-center text-gray-400 text-sm">
                    No active offers currently available for this store.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {activeOffers.map((offer) => {
                      const badgeText =
                        offer.offerTypeCode === "BOGO"
                          ? "BUY 1 GET 1"
                          : offer.offerPrice && offer.originalPrice
                          ? `${Math.round(
                              ((offer.originalPrice - offer.offerPrice) /
                                offer.originalPrice) *
                                100
                            )}% OFF`
                          : "20% OFF";

                      const offerImg =
                        offer.images && offer.images.length > 0
                          ? offer.images[0]
                          : "/deals/deal_salon_cosmetics.jpg";

                      return (
                        <div
                          key={offer._id}
                          onClick={() => setSelectedOffer(offer)}
                          className="group relative cursor-pointer bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-all flex flex-col"
                        >
                          {/* Image Container with Badge */}
                          <div className="relative aspect-[4/3] w-full overflow-hidden bg-gray-100">
                            <img
                              src={offerImg}
                              alt={offer.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            <div className="absolute top-2.5 left-2.5">
                              <span className="inline-block px-2.5 py-0.5 rounded text-[10px] sm:text-xs font-bold uppercase tracking-wider bg-[#1B6F3E] text-white shadow-sm">
                                {badgeText}
                              </span>
                            </div>
                          </div>

                          {/* Title & Description */}
                          <div className="p-3.5 flex-1 flex flex-col justify-between">
                            <div>
                              <h3 className="font-bold text-sm text-gray-900 group-hover:text-purple-700 transition-colors line-clamp-1">
                                {offer.title}
                              </h3>
                              <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                {offer.description || "Special offer available in-store via SetGo OTP."}
                              </p>
                            </div>
                            <div className="mt-3 flex items-center justify-between text-xs pt-2 border-t border-gray-50 text-purple-700 font-semibold">
                              <span>Redeem in app</span>
                              <span>→</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Explore Products Section */}
              <div className="bg-white rounded-2xl md:rounded-3xl p-6 sm:p-7 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                    Explore products
                  </h2>
                  <span className="text-xs text-gray-500 font-medium">
                    Available in store
                  </span>
                </div>

                {activeProducts.length === 0 ? (
                  <div className="py-8 text-center text-gray-400 text-sm">
                    No products listed by this store yet.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {activeProducts.map((prod) => {
                      const prodImg =
                        prod.images && prod.images.length > 0
                          ? prod.images[0]
                          : "/deals/deal_salon_cosmetics.jpg";

                      return (
                        <div
                          key={prod._id}
                          className="bg-[#FAFAFC] hover:bg-white rounded-2xl p-3 border border-gray-100 hover:border-purple-200 transition-all duration-200 flex flex-col justify-between group shadow-sm hover:shadow-md"
                        >
                          <div className="aspect-square w-full rounded-xl overflow-hidden bg-white flex items-center justify-center p-2 mb-2.5">
                            <img
                              src={prodImg}
                              alt={prod.title}
                              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-200"
                            />
                          </div>

                          <div>
                            <h3 className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                              {prod.title}
                            </h3>
                            <div className="mt-1 flex items-baseline gap-2">
                              <span className="text-xs sm:text-sm font-extrabold text-[#552FB5]">
                                ₹ {prod.price}
                              </span>
                              {prod.hasOffer && prod.offerPrice && (
                                <span className="text-[11px] text-gray-400 line-through">
                                  ₹ {prod.offerPrice}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Gallery Section */}
              {activeGallery.length > 0 && (
                <div className="bg-white rounded-2xl md:rounded-3xl p-6 sm:p-7 shadow-sm border border-gray-100">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                    Gallery
                  </h2>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                    {activeGallery.slice(0, 4).map((img, i) => (
                      <div
                        key={i}
                        onClick={() => setLightboxImageIndex(i)}
                        className="group relative aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer bg-gray-100 border border-gray-100 shadow-sm"
                      >
                        <img
                          src={img}
                          alt={`${name} gallery photo ${i + 1}`}
                          className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="p-2 rounded-full bg-white/80 text-gray-900 backdrop-blur-sm shadow">
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
                              />
                            </svg>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column (Sidebar: Address & Map, Follow, Customer Reviews, App Promo) */}
            <div className="lg:col-span-4 space-y-6">
              {/* Address Card */}
              <div className="bg-white rounded-2xl md:rounded-3xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Address</h3>
                <div className="flex items-start gap-2 text-xs sm:text-sm text-gray-600 mb-4">
                  <svg
                    className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span>{activeAddress}</span>
                </div>

                {/* Visual Map Preview */}
                <div className="relative rounded-2xl overflow-hidden border border-gray-200 bg-[#E8EDF5] aspect-[16/10] shadow-inner group">
                  {activeCoordinates ? (
                    <iframe
                      title={`${name} Map`}
                      src={`https://maps.google.com/maps?q=${activeCoordinates.lat},${activeCoordinates.lng}&hl=en&z=15&output=embed`}
                      className="w-full h-full border-0 rounded-2xl"
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      {/* Subtle map pattern lines */}
                      <svg
                        className="absolute inset-0 w-full h-full opacity-60 pointer-events-none"
                        viewBox="0 0 400 250"
                        preserveAspectRatio="none"
                      >
                        <rect width="400" height="250" fill="#E8EDF5" />
                        {/* Water ribbon */}
                        <path
                          d="M -20 180 Q 80 160 200 200 T 420 190"
                          fill="none"
                          stroke="#CBE0F5"
                          strokeWidth="28"
                        />
                        {/* Roads */}
                        <path
                          d="M 0 90 L 400 130"
                          stroke="#FFFFFF"
                          strokeWidth="8"
                        />
                        <path
                          d="M 120 0 L 150 250"
                          stroke="#FFFFFF"
                          strokeWidth="10"
                        />
                        <path
                          d="M 280 0 L 260 250"
                          stroke="#FFFFFF"
                          strokeWidth="8"
                        />
                        <path
                          d="M 50 200 L 380 40"
                          stroke="#FFF0C2"
                          strokeWidth="6"
                        />
                      </svg>

                      {/* Central Map Pin */}
                      <div className="relative z-10 flex flex-col items-center animate-bounce duration-1000">
                        <div className="w-10 h-10 rounded-full bg-[#552FB5] text-white flex items-center justify-center shadow-lg border-2 border-white">
                          <svg
                            className="w-5 h-5 fill-current"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="w-4 h-1.5 bg-black/20 rounded-full blur-[1px] mt-1" />
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-3 flex items-center justify-between">
                  <a
                    href={mapsQueryUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs sm:text-sm font-semibold text-[#552FB5] hover:text-[#422194] transition-colors"
                  >
                    Open in Maps →
                  </a>
                  {activeCoordinates && (
                    <a
                      href={mapsQueryUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-gray-500 hover:text-purple-700 font-mono transition-colors"
                      title="Coordinates (Latitude, Longitude)"
                    >
                      {activeCoordinates.lat.toFixed(4)}°, {activeCoordinates.lng.toFixed(4)}°
                    </a>
                  )}
                </div>
              </div>

              {/* Follow Section */}
              {(socialLinks?.instagram || socialLinks?.facebook) && (
                <div className="bg-white rounded-2xl md:rounded-3xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Follow</h3>
                  <div className="flex flex-wrap items-center gap-2.5">
                    {socialLinks.instagram && (
                      <a
                        href={socialLinks.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 hover:border-pink-300 hover:bg-pink-50/50 text-xs sm:text-sm font-medium text-gray-700 transition-colors"
                      >
                        <svg className="w-4 h-4 text-pink-600 fill-current" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                        </svg>
                        Instagram
                      </a>
                    )}

                    {socialLinks.facebook && (
                      <a
                        href={socialLinks.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 text-xs sm:text-sm font-medium text-gray-700 transition-colors"
                      >
                        <svg className="w-4 h-4 text-blue-600 fill-current" viewBox="0 0 24 24">
                          <path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z" />
                        </svg>
                        Facebook
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Customer Reviews Card */}
              <div className="bg-white rounded-2xl md:rounded-3xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">
                    Customer reviews
                  </h3>
                  <a
                    href={getSmartAppDownloadUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-gray-400 hover:text-purple-600 transition-colors"
                  >
                    Write a review in the app
                  </a>
                </div>

                {activeReviews.length === 0 ? (
                  <div className="py-6 text-center text-gray-400 text-xs">
                    No customer reviews yet. Be the first to review on the SetGo app!
                  </div>
                ) : (
                  <div className="space-y-3.5">
                    {activeReviews.slice(0, 3).map((rev, idx) => {
                      const reviewerName = rev.publicUserId?.name || "Customer";
                      const initial = reviewerName.charAt(0).toUpperCase();
                      const avatarBg = idx % 2 === 0 ? "bg-[#FEF6E7] text-[#925C0C]" : "bg-[#EBF2FE] text-[#1E429F]";

                      return (
                        <div
                          key={rev._id || idx}
                          className="p-3.5 rounded-2xl bg-[#FAFAFC] border border-gray-100"
                        >
                          <div className="flex items-center gap-2.5 mb-1.5">
                            <div
                              className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs ${avatarBg}`}
                            >
                              {initial}
                            </div>
                            <div>
                              <span className="text-xs font-bold text-gray-900 mr-2">
                                {reviewerName}
                              </span>
                              {rev.rating && (
                                <span className="text-[11px] font-bold text-[#D97706]">
                                  ★ {rev.rating.toFixed(1)}
                                </span>
                              )}
                            </div>
                          </div>
                          {rev.comment && (
                            <p className="text-xs text-gray-600 leading-snug pl-9">
                              {rev.comment}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Book a slot / Redeem in App Promo Card */}
              <div className="bg-[#18123A] rounded-2xl md:rounded-3xl p-6 text-white shadow-xl relative overflow-hidden">
                <div className="absolute -top-12 -right-12 w-32 h-32 bg-purple-500/20 rounded-full blur-2xl pointer-events-none" />
                <h3 className="text-base sm:text-lg font-extrabold leading-snug tracking-tight">
                  Book a slot or redeem an offer in the app
                </h3>
                <p className="text-xs text-purple-200/80 mt-1.5 mb-5 leading-relaxed">
                  Earn coins on every visit to {name}.
                </p>

                {/* App store badges */}
                <div className="flex flex-col sm:flex-row gap-2.5">
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white border border-white/20 text-xs font-semibold transition-all"
                  >
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.61-.75 1.04-1.8 0.92-2.87-.91.04-2.02.61-2.67 1.37-.58.67-1.09 1.74-.96 2.78 1.02.08 2.08-.53 2.71-1.28" />
                    </svg>
                    <span>App Store</span>
                  </a>

                  <a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white border border-white/20 text-xs font-semibold transition-all"
                  >
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                      <path d="M3.609 1.814L13.792 12 3.61 22.186c-.342-.32-.553-.787-.553-1.309V3.123c0-.522.211-.989.552-1.309zm11.238 11.24l2.253 2.254-11.83 6.828 9.577-9.082zm0-2.108L5.27 1.864l11.83 6.828-2.253 2.254zm1.493 1.054l3.056 1.764c.895.517.895 1.365 0 1.882l-3.056 1.764-1.854-1.705 1.854-1.705z" />
                    </svg>
                    <span>Google Play</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Offer Redemption Modal */}
      <AnimatePresence>
        {selectedOffer && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl relative overflow-hidden"
            >
              <button
                onClick={() => setSelectedOffer(null)}
                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-900 flex items-center justify-center transition-colors"
              >
                ✕
              </button>

              <div className="aspect-[16/9] w-full rounded-2xl overflow-hidden bg-gray-100 mb-4">
                <img
                  src={
                    selectedOffer.images?.[0] ||
                    "/deals/deal_salon_cosmetics.jpg"
                  }
                  alt={selectedOffer.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="inline-block px-2.5 py-0.5 rounded text-xs font-bold bg-[#1B6F3E] text-white uppercase tracking-wider mb-2">
                {selectedOffer.offerTypeCode === "BOGO"
                  ? "BUY 1 GET 1"
                  : selectedOffer.originalPrice && selectedOffer.offerPrice
                  ? `${Math.round(
                      ((selectedOffer.originalPrice - selectedOffer.offerPrice) /
                        selectedOffer.originalPrice) *
                        100
                    )}% OFF`
                  : "Special Offer"}
              </div>

              <h3 className="text-xl font-extrabold text-gray-900">
                {selectedOffer.title}
              </h3>

              <p className="text-gray-600 text-sm mt-2 leading-relaxed">
                {selectedOffer.description ||
                  "Redeem this exclusive voucher directly at the counter using SetGo."}
              </p>

              {/* How to redeem steps */}
              <div className="mt-5 p-4 rounded-2xl bg-[#F6F7FA] border border-gray-100 space-y-2.5 text-xs text-gray-700">
                <div className="font-bold text-gray-900">
                  How to redeem in-store:
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-[10px]">
                    1
                  </span>
                  <span>Visit {name} at {activeAddress.split(",")[0]}.</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-[10px]">
                    2
                  </span>
                  <span>Open the SetGo app and tap "Redeem Now".</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-[10px]">
                    3
                  </span>
                  <span>Share the merchant's 4-digit OTP at checkout.</span>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-2.5 sm:gap-3">
                <a
                  href={getSmartAppDownloadUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setSelectedOffer(null)}
                  className="flex-1 min-w-[140px] py-3 rounded-full bg-[#18123A] hover:bg-[#251E5C] text-white font-bold text-sm text-center shadow-md transition-colors"
                >
                  Open in SetGo App
                </a>
                <Link
                  to={`/offers/${selectedOffer._id}`}
                  className="py-3 px-5 rounded-full border border-purple-200 text-purple-700 hover:bg-purple-50 font-bold text-sm text-center transition-colors"
                >
                  View Offer Details
                </Link>
                <button
                  type="button"
                  onClick={() => setSelectedOffer(null)}
                  className="px-5 py-3 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-sm transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Gallery Lightbox Modal */}
      <AnimatePresence>
        {lightboxImageIndex !== null && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
            onClick={() => setLightboxImageIndex(null)}
          >
            <button
              onClick={() => setLightboxImageIndex(null)}
              className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/20 hover:bg-white/30 text-white text-xl flex items-center justify-center transition-colors z-10"
            >
              ✕
            </button>

            {/* Prev Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxImageIndex(
                  (prev) =>
                    (prev! - 1 + activeGallery.length) % activeGallery.length
                );
              }}
              className="absolute left-4 sm:left-8 w-11 h-11 rounded-full bg-white/20 hover:bg-white/30 text-white text-2xl flex items-center justify-center transition-colors z-10"
            >
              ‹
            </button>

            {/* Next Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxImageIndex(
                  (prev) => (prev! + 1) % activeGallery.length
                );
              }}
              className="absolute right-4 sm:right-8 w-11 h-11 rounded-full bg-white/20 hover:bg-white/30 text-white text-2xl flex items-center justify-center transition-colors z-10"
            >
              ›
            </button>

            <motion.img
              key={lightboxImageIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              src={activeGallery[lightboxImageIndex]}
              alt={`${name} gallery fullview`}
              className="max-h-[85vh] max-w-[90vw] object-contain rounded-2xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </AnimatePresence>

      {/* App Download Banner */}
      <AppDownloadBanner />

      {/* Footer */}
      <Footer />
    </div>
  );
}
